import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ExternalLink, Link2, ChevronDown, ChevronRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const fmtDay = (d) => d.split("-").reverse().join("/");

export function CommanderLinks({ eventId }) {
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const load = () => api.get(`/admin/events/${eventId}/signon/commander-links`)
      .then(({ data }) => { setLinks(data); setFailed(false); }).catch(() => setFailed(true));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [eventId]);

  async function toggle(token, enabled) {
    try {
      await api.patch(`/admin/events/${eventId}/signon/commander-links/${token}`, { enabled });
      setLinks(links.map((l) => (l.token === token ? { ...l, enabled } : l)));
      toast.success(enabled ? "Link switched on" : "Link switched off");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  }

  return (
    <section className="border border-[#002FA7]/25 bg-white" data-testid="commander-links-panel">
      <button onClick={() => setOpen(!open)} data-testid="commander-links-toggle" className="flex w-full items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Link2 className="h-4 w-4 text-[#002FA7]" />
        <span className="text-xs font-black uppercase tracking-widest text-[#002FA7]">Stage Commander live links ({links.length})</span>
        <span className="ml-auto hidden text-xs text-zinc-500 sm:inline">Private, view-only boards — also included in their status SMS and email</span>
      </button>
      {open ? (
        <div className="border-t border-black/10">
          {failed ? <p className="px-4 py-3 text-xs text-red-700" data-testid="commander-links-error">Could not load commander links — retrying.</p> : null}
          {!links.length && !failed ? <p className="px-4 py-3 text-xs text-zinc-500" data-testid="commander-links-empty">No volunteers are rostered in a "Stage Commander" or "Deputy Stage Commander" role yet.</p> : null}
          <ul className="divide-y divide-black/5">
            {links.map((l) => (
              <li key={l.token} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`commander-link-${l.token}`}>
                <div className="min-w-[180px]">
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-xs text-zinc-500">{l.elements.map((e) => `${e.role} · ${e.session} ${fmtDay(e.day)}`).join(" | ")}</div>
                </div>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <button onClick={() => navigator.clipboard.writeText(l.url).then(() => toast.success(`Link for ${l.name} copied`))} disabled={!l.enabled} data-testid={`commander-link-copy-${l.token}`} className="inline-flex items-center gap-1 border border-black/20 px-2 py-1.5 text-xs hover:bg-black hover:text-white disabled:opacity-40"><Copy className="h-3.5 w-3.5" /> Copy link</button>
                  <a href={l.url} target="_blank" rel="noreferrer" data-testid={`commander-link-open-${l.token}`} className={`inline-flex items-center gap-1 border border-black/20 px-2 py-1.5 text-xs hover:bg-black hover:text-white ${l.enabled ? "" : "pointer-events-none opacity-40"}`}><ExternalLink className="h-3.5 w-3.5" /> Open</a>
                  <label className="flex items-center gap-1.5 text-xs"><Switch checked={l.enabled} onCheckedChange={(v) => toggle(l.token, v)} data-testid={`commander-link-switch-${l.token}`} /> {l.enabled ? "On" : "Off"}</label>
                </div>
              </li>
            ))}
          </ul>
          {links[0]?.expires_at ? <p className="border-t border-black/5 px-4 py-2 text-[11px] text-zinc-500">Links stop working at the end of the day after the event finishes.</p> : null}
        </div>
      ) : null}
    </section>
  );
}
